import { useEffect } from 'react';
import { useMultiLanguageWebsite, Widget, Section, Column } from '@/hooks/useMultiLanguageWebsite';

interface MultiLanguageWebsiteRendererProps {
  pageId: string;
}

const MultiLanguageWebsiteRenderer = ({ pageId }: MultiLanguageWebsiteRendererProps) => {
  const {
    website,
    currentLanguage,
    devicePreview,
    getPage,
    getCurrentLanguageConfig,
    getResponsiveValue
  } = useMultiLanguageWebsite();

  const page = getPage(pageId);
  const languageConfig = getCurrentLanguageConfig();
  const currentWebsite = website.websites[currentLanguage];

  // Apply global styles and language settings
  useEffect(() => {
    // Set document direction and language
    document.documentElement.dir = languageConfig.direction;
    document.documentElement.lang = languageConfig.code;
    document.body.className = `${languageConfig.direction} ${languageConfig.code}-page`;

    // Apply global typography and theme
    const root = document.documentElement;
    
    // Set CSS custom properties for colors
    Object.entries(currentWebsite.theme.colors).forEach(([key, value]) => {
      root.style.setProperty(`--color-${key}`, value);
    });
    
    // Set CSS custom properties for spacing
    root.style.setProperty('--spacing-unit', `${currentWebsite.theme.spacing.unit}px`);
    currentWebsite.theme.spacing.scale.forEach((value, index) => {
      root.style.setProperty(`--spacing-${index}`, `${value}px`);
    });

    // Load Google Fonts dynamically
    const loadGoogleFonts = () => {
      const existingLink = document.querySelector('#google-fonts');
      if (existingLink) {
        existingLink.remove();
      }

      const fontFamilies = currentWebsite.theme.typography.fontFamilies;
      const fontQuery = fontFamilies
        .map(font => font.replace(' ', '+') + ':wght@100;200;300;400;500;600;700;800;900')
        .join('&family=');

      const link = document.createElement('link');
      link.id = 'google-fonts';
      link.rel = 'stylesheet';
      link.href = `https://fonts.googleapis.com/css2?family=${fontQuery}&display=swap`;
      document.head.appendChild(link);
    };

    loadGoogleFonts();
  }, [currentLanguage, currentWebsite, languageConfig]);

  // Update page title and meta tags
  useEffect(() => {
    if (page) {
      document.title = page.seo.metaTitle;
      
      // Update meta description
      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement('meta');
        metaDescription.setAttribute('name', 'description');
        document.head.appendChild(metaDescription);
      }
      metaDescription.setAttribute('content', page.seo.metaDescription);
      
      // Update meta keywords
      let metaKeywords = document.querySelector('meta[name="keywords"]');
      if (!metaKeywords) {
        metaKeywords = document.createElement('meta');
        metaKeywords.setAttribute('name', 'keywords');
        document.head.appendChild(metaKeywords);
      }
      metaKeywords.setAttribute('content', page.seo.keywords.join(', '));
      
      // Update Open Graph tags
      const ogTags = [
        { property: 'og:title', content: page.seo.ogTitle },
        { property: 'og:description', content: page.seo.ogDescription },
        { property: 'og:image', content: page.seo.ogImage },
        { property: 'og:url', content: window.location.href },
        { property: 'og:type', content: 'website' },
        { property: 'og:locale', content: languageConfig.code === 'ar' ? 'ar_SA' : 'en_US' }
      ];

      ogTags.forEach(({ property, content }) => {
        if (content) {
          let ogTag = document.querySelector(`meta[property="${property}"]`);
          if (!ogTag) {
            ogTag = document.createElement('meta');
            ogTag.setAttribute('property', property);
            document.head.appendChild(ogTag);
          }
          ogTag.setAttribute('content', content);
        }
      });

      // Add structured data
      if (page.seo.structuredData) {
        let structuredDataScript = document.querySelector('#structured-data');
        if (!structuredDataScript) {
          structuredDataScript = document.createElement('script');
          structuredDataScript.id = 'structured-data';
          structuredDataScript.type = 'application/ld+json';
          document.head.appendChild(structuredDataScript);
        }
        structuredDataScript.textContent = JSON.stringify(page.seo.structuredData);
      }
    }
  }, [page, languageConfig]);

  if (!page) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground mb-4">
            {currentLanguage === 'ar' ? 'الصفحة غير موجودة' : 'Page Not Found'}
          </h1>
          <p className="text-muted-foreground">
            {currentLanguage === 'ar' ? 'الصفحة المطلوبة غير متاحة' : 'The requested page is not available'}
          </p>
        </div>
      </div>
    );
  }

  // Render widget based on type and current device
  const renderWidget = (widget: Widget) => {
    if (!widget.conditions?.device.includes(devicePreview)) {
      return null;
    }

    const widgetStyle: React.CSSProperties = {
      margin: `${getResponsiveValue(widget.layout.margin).top}px ${getResponsiveValue(widget.layout.margin).right}px ${getResponsiveValue(widget.layout.margin).bottom}px ${getResponsiveValue(widget.layout.margin).left}px`,
      padding: `${getResponsiveValue(widget.layout.padding).top}px ${getResponsiveValue(widget.layout.padding).right}px ${getResponsiveValue(widget.layout.padding).bottom}px ${getResponsiveValue(widget.layout.padding).left}px`,
      width: getResponsiveValue(widget.layout.width),
      height: getResponsiveValue(widget.layout.height),
      maxWidth: getResponsiveValue(widget.layout.maxWidth),
      minHeight: getResponsiveValue(widget.layout.minHeight),
      backgroundColor: widget.layout.backgroundColor,
      backgroundImage: widget.layout.backgroundImage ? `url(${widget.layout.backgroundImage})` : undefined,
      borderRadius: `${getResponsiveValue(widget.layout.borderRadius)}px`,
      border: `${getResponsiveValue(widget.layout.border.width)}px ${widget.layout.border.style} ${widget.layout.border.color}`,
      boxShadow: widget.layout.boxShadow,
      zIndex: widget.layout.zIndex,
      display: getResponsiveValue(widget.layout.display),
      flexDirection: getResponsiveValue(widget.layout.flexDirection),
      justifyContent: getResponsiveValue(widget.layout.justifyContent),
      alignItems: getResponsiveValue(widget.layout.alignItems),
      gap: `${getResponsiveValue(widget.layout.gap)}px`
    };

    const typographyStyle: React.CSSProperties = widget.typography ? {
      fontFamily: widget.typography.fontFamily,
      fontSize: `${getResponsiveValue(widget.typography.fontSize)}px`,
      fontWeight: widget.typography.fontWeight,
      lineHeight: widget.typography.lineHeight,
      letterSpacing: `${widget.typography.letterSpacing}px`,
      textAlign: widget.typography.textAlign,
      textTransform: widget.typography.textTransform,
      color: widget.typography.color
    } : {};

    const combinedStyle = { ...widgetStyle, ...typographyStyle };

    switch (widget.type) {
      case 'heading':
        const HeadingTag = widget.content.tag || 'h2';
        return (
          <HeadingTag key={widget.id} style={combinedStyle}>
            {widget.content.text}
          </HeadingTag>
        );

      case 'text':
        return (
          <p key={widget.id} style={combinedStyle}>
            {widget.content.text}
          </p>
        );

      case 'button':
        return (
          <a
            key={widget.id}
            href={widget.content.url}
            target={widget.content.target || '_self'}
            style={combinedStyle}
            className="inline-block text-center transition-all duration-300 hover:opacity-80 cursor-pointer"
          >
            {widget.content.text}
          </a>
        );

      case 'image':
        return (
          <div key={widget.id} style={widgetStyle}>
            <img
              src={widget.image?.src || '/placeholder.svg'}
              alt={widget.image?.alt || ''}
              style={{
                width: '100%',
                height: '100%',
                objectFit: widget.image?.objectFit || 'cover',
                objectPosition: widget.image?.objectPosition || 'center',
                borderRadius: `${getResponsiveValue(widget.image?.borderRadius || { desktop: 0, tablet: 0, mobile: 0 })}px`
              }}
            />
            {widget.image?.overlay.enabled && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: widget.image.overlay.color,
                  opacity: widget.image.overlay.opacity / 100,
                  borderRadius: `${getResponsiveValue(widget.image?.borderRadius || { desktop: 0, tablet: 0, mobile: 0 })}px`
                }}
              />
            )}
          </div>
        );

      case 'spacer':
        return (
          <div
            key={widget.id}
            style={widgetStyle}
            aria-hidden="true"
          />
        );

      case 'divider':
        return (
          <hr
            key={widget.id}
            style={{
              ...widgetStyle,
              border: 'none',
              borderTop: `1px solid ${widget.layout.backgroundColor || currentWebsite.theme.colors.border}`,
              margin: `${getResponsiveValue(widget.layout.margin).top}px 0 ${getResponsiveValue(widget.layout.margin).bottom}px 0`
            }}
          />
        );

      case 'icon':
        return (
          <div key={widget.id} style={combinedStyle}>
            {/* Icon implementation would go here */}
            <span>{widget.content.icon || '★'}</span>
          </div>
        );

      default:
        return (
          <div key={widget.id} style={combinedStyle}>
            <p className="text-muted-foreground text-sm">
              {widget.type} widget - {widget.name}
            </p>
          </div>
        );
    }
  };

  // Render column
  const renderColumn = (column: Column) => {
    const columnStyle: React.CSSProperties = {
      width: `${getResponsiveValue(column.width)}%`,
      padding: `${getResponsiveValue(column.layout.padding).top}px ${getResponsiveValue(column.layout.padding).right}px ${getResponsiveValue(column.layout.padding).bottom}px ${getResponsiveValue(column.layout.padding).left}px`,
      margin: `${getResponsiveValue(column.layout.margin).top}px ${getResponsiveValue(column.layout.margin).right}px ${getResponsiveValue(column.layout.margin).bottom}px ${getResponsiveValue(column.layout.margin).left}px`,
      backgroundColor: column.layout.backgroundColor,
      borderRadius: `${getResponsiveValue(column.layout.borderRadius)}px`,
      border: `${getResponsiveValue(column.layout.border.width)}px ${column.layout.border.style} ${column.layout.border.color}`,
      boxShadow: column.layout.boxShadow,
      display: getResponsiveValue(column.layout.display),
      flexDirection: getResponsiveValue(column.layout.flexDirection),
      justifyContent: getResponsiveValue(column.layout.justifyContent),
      alignItems: getResponsiveValue(column.layout.alignItems),
      gap: `${getResponsiveValue(column.layout.gap)}px`
    };

    return (
      <div key={column.id} style={columnStyle}>
        {column.widgets.map(renderWidget)}
      </div>
    );
  };

  // Render section
  const renderSection = (section: Section) => {
    if (!section.enabled) return null;

    const sectionStyle: React.CSSProperties = {
      margin: `${getResponsiveValue(section.layout.margin).top}px ${getResponsiveValue(section.layout.margin).right}px ${getResponsiveValue(section.layout.margin).bottom}px ${getResponsiveValue(section.layout.margin).left}px`,
      padding: `${getResponsiveValue(section.layout.padding).top}px ${getResponsiveValue(section.layout.padding).right}px ${getResponsiveValue(section.layout.padding).bottom}px ${getResponsiveValue(section.layout.padding).left}px`,
      backgroundColor: section.layout.backgroundColor,
      backgroundImage: section.layout.backgroundImage ? `url(${section.layout.backgroundImage})` : undefined,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      minHeight: getResponsiveValue(section.layout.minHeight),
      borderRadius: `${getResponsiveValue(section.layout.borderRadius)}px`,
      border: `${getResponsiveValue(section.layout.border.width)}px ${section.layout.border.style} ${section.layout.border.color}`,
      boxShadow: section.layout.boxShadow,
      display: getResponsiveValue(section.layout.display),
      flexDirection: getResponsiveValue(section.layout.flexDirection),
      justifyContent: getResponsiveValue(section.layout.justifyContent),
      alignItems: getResponsiveValue(section.layout.alignItems),
      gap: `${getResponsiveValue(section.layout.gap)}px`,
      position: 'relative'
    };

    return (
      <section key={section.id} style={sectionStyle}>
        <div 
          style={{
            display: 'flex',
            width: '100%',
            gap: `${getResponsiveValue(section.layout.gap)}px`,
            flexDirection: devicePreview === 'mobile' ? 'column' : 'row'
          }}
        >
          {section.columns.map(renderColumn)}
        </div>
      </section>
    );
  };

  // Sort sections by order
  const sortedSections = [...page.sections].sort((a, b) => a.order - b.order);

  return (
    <div 
      className="min-h-screen"
      style={{
        backgroundColor: currentWebsite.theme.colors.background,
        color: currentWebsite.theme.colors.text,
        fontFamily: currentWebsite.theme.typography.body.fontFamily,
        fontSize: `${getResponsiveValue(currentWebsite.theme.typography.body.fontSize)}px`,
        lineHeight: currentWebsite.theme.typography.body.lineHeight,
        direction: languageConfig.direction
      }}
    >
      {/* Render global header if exists */}
      {currentWebsite.globalComponents.header.map(renderSection)}
      
      {/* Render page sections */}
      <main>
        {sortedSections.map(renderSection)}
      </main>
      
      {/* Render global footer if exists */}
      {currentWebsite.globalComponents.footer.map(renderSection)}

      {/* Custom CSS */}
      {page.settings.customCSS && (
        <style dangerouslySetInnerHTML={{ __html: page.settings.customCSS }} />
      )}

      {/* Custom JavaScript */}
      {page.settings.customJS && (
        <script dangerouslySetInnerHTML={{ __html: page.settings.customJS }} />
      )}

      {/* Global custom code */}
      {currentWebsite.settings.customCode.bodyEnd && (
        <div dangerouslySetInnerHTML={{ __html: currentWebsite.settings.customCode.bodyEnd }} />
      )}
    </div>
  );
};

export default MultiLanguageWebsiteRenderer;